import React,{useState,useMemo} from 'react'
import Subheading from '../../components/Text/Subheading'
import Icon from '../../components/Text/Icon'
import ChatTabs from '../../components/Chat/ChatTabs'
import ChatContainer from '../../components/Chat/ChatContainer'
import ActionPreview from '../../components/Chat/ActionPreview'
import CommandPalette from '../../components/Chat/CommandPalette'

export default function ChatPanel({
    width,
    height,
    onResizeMouseDown,
    onClose,
    chatTabs,
    activeTabId,
    onTabChange,
    onAddTab,
    onRemoveTab,
    chats,
    onDeleteBubble,
    chatLoading,
    currentToolCalls,
    thinkingState,
    streamingMessage,
    pendingChanges,
    currentNodes,
    onAcceptChanges,
    onRejectChanges,
    chatInput,
    onChatInputChange,
    onSubmit,
    onCancel,
    userName=null,
}) {
    const [searchQuery,setSearchQuery]=useState('')
    const [showSearch,setShowSearch]=useState(false)

    // Flatten all tab messages for cross-tab search results
    const searchResults=useMemo(() => {
        if(!searchQuery.trim()) return []
        const q=searchQuery.toLowerCase()
        const results=[]
        chatTabs.forEach(tab => {
            tab.messages.forEach((msg,i) => {
                if(msg.content?.toLowerCase().includes(q)) {
                    results.push({...msg,_tabId: tab.id,_tabName: tab.name,_msgIndex: i})
                }
            })
        })
        return results
    },[searchQuery,chatTabs])

    const inSearchMode=showSearch&&searchQuery.trim().length>0

    return (
        <div style={{height,width: `${width}px`}} className="flex overflow-hidden relative flex-col flex-shrink-0 bg-white border-r shadow-md border-dark-100">
            {/* Resize Handle */}
            <div
                onMouseDown={onResizeMouseDown}
                className="absolute top-0 bottom-0 -left-1 z-50 w-2 bg-transparent transition-colors cursor-ew-resize hover:bg-primary-400 group"
                title="Drag to resize"
            >
                <div className="absolute top-0 bottom-0 left-0 w-0.5 bg-gray-300 group-hover:bg-primary-500" />
            </div>

            {/* Header */}
            <div className='flex flex-row gap-2 justify-between items-center px-4 pt-3 pb-2 w-full'>
                {showSearch? (
                    <div className='flex flex-1 items-center gap-2'>
                        <input
                            autoFocus
                            type='text'
                            value={searchQuery}
                            onChange={e=>setSearchQuery(e.target.value)}
                            placeholder='Search all chats…'
                            className='flex-1 text-sm px-2 py-1 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary'
                            onKeyDown={e=>e.key==='Escape'&&(setShowSearch(false),setSearchQuery(''))}
                        />
                        <button onClick={()=>{setShowSearch(false);setSearchQuery('')}} className='p-1 rounded hover:bg-gray-100 transition-colors'>
                            <Icon icon='x' className='text-sm text-gray-500' />
                        </button>
                    </div>
                ):(
                    <>
                        <Subheading className='!pb-0'>Flowsage AI Assistant</Subheading>
                        <div className='flex flex-row gap-2 items-center'>
                            <button onClick={()=>setShowSearch(true)} className='p-1 rounded hover:bg-gray-100 transition-colors' title='Search chats (⌘F)'>
                                <Icon icon='search' className='text-sm text-gray-500' />
                            </button>
                            <div className='px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded'>
                                <kbd className='font-mono font-semibold'>⌘K</kbd> to toggle
                            </div>
                            <div onClick={onClose} className='p-1 rounded transition-colors cursor-pointer hover:bg-gray-100'>
                                <Icon icon='x' className='text-xl text-gray-600' />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Chat Tabs — hidden during search to reduce noise */}
            {!inSearchMode&&(
                <ChatTabs
                    tabs={chatTabs}
                    activeTabId={activeTabId}
                    onTabChange={onTabChange}
                    onAddTab={onAddTab}
                    onRemoveTab={onRemoveTab}
                />
            )}

            {/* Cross-tab search results */}
            {inSearchMode? (
                <div className='flex flex-col flex-1 min-h-0 overflow-y-auto px-2 py-2 gap-1'>
                    {searchResults.length===0? (
                        <p className='text-sm text-gray-400 text-center mt-8'>No messages match "{searchQuery}"</p>
                    ):searchResults.map((msg,i) => (
                        <button
                            key={i}
                            onClick={()=>{onTabChange(msg._tabId);setShowSearch(false);setSearchQuery('');}}
                            className='flex flex-col gap-0.5 p-2 rounded-md border border-gray-100 hover:bg-gray-50 text-left transition-colors'
                        >
                            <span className='text-[10px] font-semibold text-primary uppercase tracking-wide'>{msg._tabName}</span>
                            <span className='text-xs text-gray-700 line-clamp-2'>{msg.content}</span>
                        </button>
                    ))}
                </div>
            ):(
                /* Normal chat view */
                <div className='flex flex-col flex-1 min-h-0 px-2'>
                    <ChatContainer
                        chats={chats}
                        onDeleteBubble={onDeleteBubble}
                        isProcessing={chatLoading}
                        currentToolCalls={currentToolCalls}
                        thinkingState={thinkingState}
                        streamingMessage={streamingMessage}
                        userName={userName}
                        searchQuery={searchQuery}
                    />
                </div>
            )}

            {/* Action Preview */}
            {!inSearchMode&&pendingChanges&&(
                <div className='px-2'>
                    <ActionPreview
                        changes={pendingChanges}
                        currentNodes={currentNodes}
                        onAccept={onAcceptChanges}
                        onReject={onRejectChanges}
                    />
                </div>
            )}

            {/* Command Palette */}
            {!inSearchMode&&(
                <div className='px-4 pt-3 pb-4 border-t border-gray-200'>
                    <CommandPalette
                        value={chatInput}
                        onChange={onChatInputChange}
                        onSubmit={onSubmit}
                        onCancel={onCancel}
                        loading={chatLoading}
                        autoFocus={false}
                    />
                </div>
            )}
        </div>
    )
}
